import { useEffect, useState } from "react";
import {
  MdAdd,
  MdCheck,
  MdMailOutline,
  MdOutlineLocalPhone,
} from "react-icons/md";
import { formatPhoneNumber } from "utils/utils";
import { ContactListType } from "utils/interfaces";

interface UserCardProps {
  user: ContactListType;
  selectedUsers: ContactListType[];
  activatePrimaryTag?: boolean;
  justDisplay?: boolean;
  selectAction?: (user: ContactListType, action: "add" | "remove") => void;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  selectedUsers,
  activatePrimaryTag = true,
  justDisplay = false,
  selectAction,
}) => {
  // States definition
  const [selected, setSelected] = useState(false);
  const [isPrimary, setIsPrimary] = useState(false);

  /**
   * Define the select status of this card
   */
  useEffect(() => {
    // Get the selected data
    const selectedContactDataIds = selectedUsers.map(
      (contactItem: ContactListType) => {
        // Check if it's created or temporal
        if (contactItem._id) return contactItem._id;
        else return contactItem.tempId;
      }
    );

    // Check if it's going to be temporal or a created one
    const contactId = user._id ? user._id : user.tempId;

    // Set selected if it's included in the list
    if (selectedContactDataIds.includes(contactId)) {
      setSelected(true);

      // And set as primary if there is the first element in the list
      // and if the option is selected to display the primary tag
      if (activatePrimaryTag)
        setIsPrimary(selectedContactDataIds[0] === contactId);
    } else {
      // Otherwise, select force them to be false
      setSelected(false);
      // Set if the option is selected to display the primary tag
      if (activatePrimaryTag) setIsPrimary(false);
    }
  }, [selectedUsers]);

  /**
   * Function used to operate on click of the checkbox
   */
  const handleOnClick = () => {
    if (selectAction && !justDisplay) {
      // If it is currently not selected, then add to the array of selected data, otherwise, delete from the list
      selectAction(user, !selected ? "add" : "remove");
    }
  };

  return (
    <button
      className={`self-stretch text-start p-3 bg-neutral-25 border ${
        selected && !justDisplay ? "border-neutral-700" : "border-transparent"
      } rounded-xl justify-start items-center gap-3 inline-flex ${
        justDisplay ? "cursor-default" : "cursor-pointer hover:bg-neutral-50"
      } ease-in-out duration-150`}
      onClick={handleOnClick}
    >
      <div className="grow shrink basis-0 flex-col justify-center items-start gap-2 inline-flex">
        <div className="w-[335px] h-fit justify-start items-center gap-2 inline-flex">
          <div className="overflow-hidden self-stretch break-keep whitespace-nowrap text-ellipsis text-neutral-700 text-sm font-semibold font-sans leading-[17px]">
            {user ? user.firstName + " " + user.lastName : "-"}
          </div>
          {isPrimary && (
            <div className="w-[59px] px-1.5 py-0.5 bg-accent-500 rounded justify-center items-center gap-2 flex">
              <div className="text-white text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
                Primary
              </div>
            </div>
          )}
        </div>
        <div className="self-stretch justify-start items-center gap-3 inline-flex">
          <div className="w-24 justify-left items-center gap-1 flex">
            <div className="w-4 h-4 justify-center items-center flex">
              <MdOutlineLocalPhone className="w-full h-full fill-neutral-500" />
            </div>
            <div className="text-neutral-500 text-xs font-normal font-inter leading-[14px] overflow-hidden text-ellipsis text-nowrap whitespace-nowrap">
              {formatPhoneNumber(user.phone) || "-"}
            </div>
          </div>
          <div className="w-px self-stretch justify-start items-start gap-2 flex">
            <div className="w-px self-stretch bg-neutral-200" />
          </div>
          <div className="grow shrink basis-0 h-4 justify-start items-center gap-1 flex">
            <div className="w-4 h-4 justify-center items-center flex">
              <MdMailOutline className="w-full h-full fill-neutral-500" />
            </div>
            <div className="w-[181px] text-neutral-500 text-xs font-normal font-inter leading-[14px] overflow-hidden text-ellipsis text-nowrap whitespace-nowrap">
              {user && user.email ? user.email : "-"}
            </div>
          </div>
        </div>
      </div>
      {!justDisplay && (
        <div
          className={
            selected
              ? "p-2 bg-neutral-700 rounded-full justify-start items-start gap-2 flex"
              : "p-2 bg-neutral-100 rounded-full justify-start items-start gap-2 flex"
          }
        >
          <div className="w-4 h-4 relative">
            {selected ? (
              <MdCheck className="w-full h-full fill-neutral-50" />
            ) : (
              <MdAdd className="w-full h-full fill-neutral-500" />
            )}
          </div>
        </div>
      )}
    </button>
  );
};

export default UserCard;
